import React, { createContext, useCallback, useContext, useState } from 'react';
import AppCache from '../Helper/Common/AppCache';

const DashboardContext = createContext();

/**
 * DashboardProvider
 *
 * Manages which observability dashboard is active.
 * Reads initial state from AppCache (set during login) so the choice
 * survives page refreshes.
 *
 * availableDashboards: array of { featureCode, featureName, key, route }
 *   — dashboards the user has view permission for, ordered as returned at login.
 *
 * activeDashboard: key string ('coulomb' | 'custom' | ...)
 * canSwitch: true when user has access to more than one dashboard
 */
export const DashboardProvider = ({ children }) => {
  const [activeDashboard, setActiveDashboard] = useState(() => {
    return AppCache.getItem('activeDashboard') || 'coulomb';
  });

  const [availableDashboards] = useState(() => {
    const stored = AppCache.getItem('availableDashboards');
    if (Array.isArray(stored) && stored.length > 0) return stored;
    // Fallback for sessions that pre-date availableDashboards storage
    const hasCoulomb = AppCache.getItem('hasCoulombDashboard') ?? true;
    const hasCustom = AppCache.getItem('hasCustomDashboard') ?? false;
    const fallback = [];
    if (hasCoulomb) fallback.push({ featureCode: 'COULOMB_OBSERVABILITY_PLATFORM', featureName: 'Coulomb', key: 'coulomb', route: '/' });
    if (hasCustom) {
      const allConfigs = AppCache.getItem('customDashboardsConfig') || {};
      const firstKey = Object.keys(allConfigs)[0];
      if (firstKey) {
        const firstCfg = allConfigs[firstKey];
        fallback.push({
          featureCode: firstCfg.controlledByFeatureName || firstKey,
          featureName: firstKey,
          key: firstKey,
          route: firstCfg.route || '/custom-observability',
        });
      }
    }
    return fallback;
  });

  const canSwitch = availableDashboards.length > 1;

  // Derived backward-compat flags
  const hasCoulombDashboard = availableDashboards.some(d => d.key === 'coulomb');
  const hasCustomDashboard  = availableDashboards.some(d => d.key !== 'coulomb');

  const switchDashboard = useCallback((key) => {
    setActiveDashboard(key);
    AppCache.setItem('activeDashboard', key);
    // Sync the active custom dashboard's config and navigation so page components
    // read the right data on the next render without a full login cycle.
    if (key !== 'coulomb') {
      const allConfigs = AppCache.getItem('customDashboardsConfig') || {};
      if (allConfigs[key]) AppCache.setItem('customObservabilityConfig', allConfigs[key]);
      const allNavs = AppCache.getItem('allCustomNavigationCards') || {};
      if (allNavs[key]) AppCache.setItem('customNavigationCard', allNavs[key]);
      const allPagesMeta = AppCache.getItem('allCustomPagesMeta') || {};
      if (allPagesMeta[key]) AppCache.setItem('customPagesMeta', allPagesMeta[key]);
    }
  }, []);

  return (
    <DashboardContext.Provider
      value={{
        activeDashboard,
        availableDashboards,
        canSwitch,
        switchDashboard,
        hasCoulombDashboard,
        hasCustomDashboard,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

/**
 * useDashboard — hook to consume DashboardContext
 */
export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export default DashboardContext;
